import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Badge, Button, Card, CardBody, Col, Row, Table } from "reactstrap"

import { CanView } from "components/Common/CanView"
import PageWrapper from "components/Common/PageWrapper"
import { ACCESS } from "constants/accessPermissions"
import apiClient from "api/axios"

const STATUS_COLORS = { P: "success", L: "danger", A: "danger", W: "secondary", H: "info", "—": "secondary" }

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" },
]

const buildYearOptions = () => {
  const current = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => current - 2 + i)
}

const AttendanceMonthlyReport = () => {
  document.title = "Monthly Attendance Report"

  const today = new Date()
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [year, setYear] = useState(today.getFullYear())
  const [yearOptions] = useState(buildYearOptions())

  const {
    data: reportData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["attendance-monthly-report", month, year],
    queryFn: async () => {
      const res = await apiClient.get("/attendance/report/monthly", {
        params: { month, year },
      })
      console.log("[MonthlyReport] response:", res.data)
      return res.data?.data ?? null
    },
  })

  const data = reportData

  const downloadPdf = async () => {
    try {
      const res = await apiClient.get("/attendance/report/monthly/pdf", {
        params: { month, year },
        responseType: "blob",
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `attendance-${year}-${String(month).padStart(2, "0")}.pdf`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      console.error("PDF download failed", err)
    }
  }

  const matrix = data?.matrix ?? []
  const totals = data?.totals ?? {}
  const firstRow = matrix[0]
  const dayHeaders = firstRow
    ? Object.keys(firstRow.days).map(d => Number(d)).sort((a, b) => a - b)
    : []

  return (
    <PageWrapper title="Monthly Attendance Report">
      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-end">
            <Col md={2}>
              <label className="form-label">Month</label>
              <select
                className="form-select"
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label">Year</label>
              <select
                className="form-select"
                value={year}
                onChange={e => setYear(Number(e.target.value))}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>
            <Col md={2}>
              <Button
                color="primary"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </Col>
            {data && (
              <Col className="text-end">
                <CanView access={[ACCESS.ATTENDANCE.EXPORT_MONTHLY_PDF]}>
                  <Button color="success" onClick={downloadPdf}>
                    Export PDF
                  </Button>
                </CanView>
              </Col>
            )}
          </Row>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 small">
              Failed to load monthly report.{" "}
              {error?.response?.data?.message ?? error?.message}
            </div>
          )}
        </CardBody>
      </Card>

      {isLoading && !data && (
        <div className="text-center text-muted py-4">Loading monthly report…</div>
      )}

      {data && (
        <>
          <Row className="mb-3">
            <Col>
              <div className="border p-3 text-center">
                <div className="fs-5 fw-bold">{totals.present ?? 0}</div>
                <div className="small text-muted">Present</div>
              </div>
            </Col>
            <Col>
              <div className="border p-3 text-center">
                <div className="fs-5 fw-bold text-danger">{totals.late ?? 0}</div>
                <div className="small text-muted">Late</div>
              </div>
            </Col>
            <Col>
              <div className="border p-3 text-center">
                <div className="fs-5 fw-bold text-danger">{totals.absent ?? 0}</div>
                <div className="small text-muted">Absent</div>
              </div>
            </Col>
            <Col>
              <div className="border p-3 text-center">
                <div className="fs-5 fw-bold">
                  {(totals.weekend ?? 0) + (totals.holiday ?? 0)}
                </div>
                <div className="small text-muted">Off</div>
              </div>
            </Col>
            <Col>
              <div className="border p-3 text-center">
                <div className="fs-5 fw-bold">{totals.late_minutes ?? 0}</div>
                <div className="small text-muted">Late Min</div>
              </div>
            </Col>
          </Row>

          <Card>
            <CardBody className="p-0">
              <div style={{ overflowX: "auto" }}>
                <Table responsive bordered size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 150 }}>Employee</th>
                      {dayHeaders.map(d => (
                        <th
                          key={d}
                          className="text-center"
                          style={{
                            width: 22,
                            fontSize: 9,
                            padding: "3px !important",
                          }}
                        >
                          {d}
                        </th>
                      ))}
                      <th className="text-center">P</th>
                      <th className="text-center">L</th>
                      <th className="text-center">A</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map(row => (
                      <tr key={row.employee_id}>
                        <td style={{ fontSize: 11 }}>
                          {row.name}
                          <br />
                          <span className="text-muted">
                            {row.employee_id}
                          </span>
                        </td>
                        {dayHeaders.map(d => (
                          <td
                            key={d}
                            className="text-center"
                            style={{
                              fontSize: 10,
                              padding: "2px !important",
                              color:
                                row.days[d] === "L" || row.days[d] === "A"
                                  ? "#dc3545"
                                  : "inherit",
                              fontWeight:
                                row.days[d] === "L" || row.days[d] === "A"
                                  ? 600
                                  : 400,
                            }}
                          >
                            {row.days[d] ?? ""}
                          </td>
                        ))}
                        <td className="text-center fw-bold">
                          {row.summary?.present ?? 0}
                        </td>
                        <td className="text-center fw-bold text-danger">
                          {row.summary?.late ?? 0}
                        </td>
                        <td className="text-center fw-bold text-danger">
                          {row.summary?.absent ?? 0}
                        </td>
                      </tr>
                    ))}
                    {matrix.length === 0 && (
                      <tr>
                        <td
                          colSpan={dayHeaders.length + 4}
                          className="text-center text-muted py-4"
                        >
                          No employees found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          <div className="mt-2 text-muted small">
            <span className="me-3">
              <Badge color="success" pill>P</Badge> Present
            </span>
            <span className="me-3">
              <Badge color="danger" pill>L</Badge> Late
            </span>
            <span className="me-3">
              <Badge color="danger" pill>A</Badge> Absent
            </span>
            <span className="me-3">
              <Badge color="secondary" pill>W</Badge> Weekend
            </span>
            <span className="me-3">
              <Badge color="info" pill>H</Badge> Holiday
            </span>
            <span className="me-3">
              <Badge color="secondary" pill>—</Badge> Before Joining / Future
            </span>
          </div>
        </>
      )}
    </PageWrapper>
  )
}

export default AttendanceMonthlyReport